import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import onSubmitTaxRegistrationForm from '../forms/onSubmitTaxRegistrationForm';
import { Field, reduxForm, formValueSelector, getFormValues } from 'redux-form';
import { FormattedMessage, injectIntl } from 'react-intl';
import { get, isEmpty, filter, map, mapKeys, debounce } from 'lodash';
import TextField from '../../app-main/components/reduxForms/fields/TextField/TextField';
import NumberField from 'shared-components/reduxForms/fields/NumberField/NumberField';
import RequiredLabel from '../../elements-inputs/RequiredLabel';
import validateTaxRegistrationForm from '../forms/ValidateTaxRegistrationForm';
import RadioField from '../../app-main/components/reduxForms/fields/RadioField/RadioField';
import AddressFormInputs from '../forms/AddressFormInputs';
import Button from '../../elements-buttons/Button';
import Toast from 'shared-components/toast/toast';
import { vatIdLengthData } from '../data/vatIdLengthData';
import { intlTaxTypeId, intlTaxTypeRates, intlTaxTypeRegisteredAddress, intlTaxTypeBusinessCopy } from '../data/translationsData';
import { hostVatSupportLink } from '../data/supportLinks';

const TaxRegistrationForm = ({
  taxType = '',
  handleSubmit = null,
  initialValues = {},
  displayBillingAddress = '',
  displayAddressForm,
  addressLine1 = '',
  addressLine2 = '',
  allCountries,
  defaultCountry = '',
  country,
  isLoading,
  taxIdLength = '',
  error = null,
  intlTranslationsKey = '',
  isProvincial,
  isEdit,
  federalVatProfile,
  federalVatProfileId = '',
  subRegionVatProfile,
  subRegionVatProfileId = '',
  vatUpdateError,
  address,
  isHost
}) => {
  const countryOptions = map(allCountries, country => {
    return { value: country.code, label: country.name };
  });

  const selectedCountry = filter(allCountries, ['code', country])[0];
  const keyMap = { code: 'value', name: 'label' };
  const divisions = get(selectedCountry, 'zones', []);
  const formattedDivisions = divisions.map(division => mapKeys(division, (value, key) => keyMap[key]));

  return (
    <form className="TaxRegistrationForm space-around" onSubmit={handleSubmit}>
      <div className="input-container no-side-padding">
        <RequiredLabel className="label" htmlFor="business-name">
          <FormattedMessage
            id="hosting.tax_registration_form.business_name_label"
            defaultMessage="Business name"
          />
        </RequiredLabel>
        <Field
          id="business-name"
          component={TextField}
          name="business_name"
        />
        <div className="margin-md-top margin-xl-bottom text-gray-600">
          <span>
            {intlTaxTypeBusinessCopy[intlTranslationsKey] && <FormattedMessage { ...intlTaxTypeBusinessCopy[intlTranslationsKey] } />}
          </span>
        </div>
      </div>

      <div className="input-container no-side-padding">
        <RequiredLabel className="label text-transform-none" htmlFor="vat-id">
          {intlTaxTypeId[intlTranslationsKey] && <FormattedMessage { ...intlTaxTypeId[intlTranslationsKey] } />}
        </RequiredLabel>
        <Field
          id="vat-id"
          component={TextField}
          name="vat_id"
        />
        <div className="margin-md-top margin-xl-bottom text-gray-600">
          <span>
            {
              taxIdLength &&
              <FormattedMessage
                id="hosting.tax_registration_form.vat_character_id_text"
                defaultMessage="{taxIdLength}-character ID"
                values={{ taxIdLength: taxIdLength }}
              />
            }
          </span>
        </div>
      </div>

      {
        isHost &&
        <div className="input-container no-side-padding">
          <RequiredLabel className="label text-transform-none" htmlFor="vat-rate">
            {intlTaxTypeRates[intlTranslationsKey] && <FormattedMessage {...intlTaxTypeRates[intlTranslationsKey] } />}
          </RequiredLabel>
          <Field
            id="vat-rate"
            component={NumberField}
            name="vat_percent"
          />
          <div className="margin-md-top margin-xl-bottom text-gray-600">
            <span>
              <FormattedMessage
                id="hosting.tax_registration_form.tax_percentage_text"
                defaultMessage="This tax percentage should be included in your hourly rate and cleaning fees."
              />
              <a className="margin-sm-left" href={hostVatSupportLink}
                target="_blank" rel="noopener noreferrer">
                <FormattedMessage
                  id="hosting.tax_registration_form.learn_more_link"
                  defaultMessage=" Learn more"
                />
              </a>
            </span>
          </div>
        </div>
      }

      <hr className="margin-xl-bottom margin-xl-top" />

      <h5 className="margin-xl-top margin-xl-bottom">
        {intlTaxTypeRegisteredAddress[intlTranslationsKey] && <FormattedMessage { ...intlTaxTypeRegisteredAddress[intlTranslationsKey] } />}
      </h5>

      {
        address &&
        <Field
          component={RadioField}
          name="addressChoice"
          className="padding-lg-bottom"
          initialValues={initialValues}
          options={[
            {
              id: 'firstAddressOption',
              value: 'same-as-billing',
              component: <div
                className={`bg-gray-100 padding-lg
                margin-xl-top margin-xl-bottom ${displayBillingAddress}`}>
                <div>{addressLine1}</div> <div>{addressLine2}</div>
              </div>,
              label: <FormattedMessage
                id="hosting.address_form.use_billing_address"
                defaultMessage="Use billing address"/>
            },
            {
              id: 'secondAddressOption',
              value: 'different-address',
              label: <FormattedMessage
                id="hosting.address_form.use_different_address"
                defaultMessage="Use a different address"/>
            }
          ]}
        />
      }

      {
        displayAddressForm &&
        <div className="AddressForm">
          <AddressFormInputs
            defaultCountry={defaultCountry}
            countryOptions={countryOptions}
            selectedCountry={selectedCountry}
            formattedDivisions={formattedDivisions}
            displayTooltip={false}
          />
        </div>
      }

      <div className="margin-xl-top">
        <Button
          category="primary"
          className="submit-cta block-xs-left full-width"
          disabled={isLoading}
          attributes={{ 'data-testing-id': 'address' }}
          isLoading={isLoading }
        >
          {
            isEdit ?
            <FormattedMessage
              id="hosting.tax_registration_form.button_text_update"
              defaultMessage="Update tax details"/>
            :
            <FormattedMessage
              id="hosting.tax_registration_form.button_text_add"
              defaultMessage="Add tax details"/>
          }
        </Button>
        <div>
          <a className="btn btn-gray-outline full-width margin-lg-top" href='/account/methods'>
            <FormattedMessage
              id="hosting.tax_registration_form.form_cancel_link_text"
              defaultMessage="Cancel"
            />
          </a>
        </div>
      </div>

      <Toast
        error={true}
        show={vatUpdateError}
        text={ <FormattedMessage
        id="hosting.tax_registration_form.toast_error_message"
        defaultMessage="Error, please try again"/> }
      />
    </form>
  )
}

TaxRegistrationForm.propTypes = {
  taxType: PropTypes.string,
  handleSubmit: PropTypes.func,
  initialValues: PropTypes.object,
  displayBillingAddress: PropTypes.string,
  displayAddressForm: PropTypes.bool,
  addressLine1: PropTypes.string,
  addressLine2: PropTypes.string,
  envCountries: PropTypes.object,
  allCountries: PropTypes.array,
  defaultCountry: PropTypes.string,
  country: PropTypes.string,
  isLoading: PropTypes.bool,
  addressState: PropTypes.string,
  subFlow: PropTypes.string,
  taxIdLength: PropTypes.string,
  isHost: PropTypes.bool
};

const config = {
  form: 'TAX_REGISTRATION_FORM',
  validate: validateTaxRegistrationForm,
  onSubmit: debounce(onSubmitTaxRegistrationForm, 500)
};

const selector = formValueSelector('TAX_REGISTRATION_FORM');

const mapStateToProps = (state, ownProps) => {
  const { profile, fee_profiles, env, auth } = state;
  const { isProvincial, federalVatProfile, subRegionVatProfile, isEdit, address, intl, isHost } = ownProps;
  const vatProfile = isProvincial ? subRegionVatProfile : federalVatProfile

  const ssoId = get(profile, 'SSOid');
  const accessToken = get(auth, 'accessToken', '');
  const billingAddress = get(fee_profiles, 'billingAddress.data', []);
  const vatProfileCountry = get(vatProfile, 'country', null);
  const defaultCountry = vatProfileCountry || get(billingAddress, '[0].country', null);
  const localeCountry = get(intl, 'locale').split('-')[1];
  const envCountries = get(env, 'web.countries');

  // we can assume hosts have a billing address (backfilled)
  const useLocaleCountry = !isHost && !defaultCountry;
  const country = selector(state, 'country') || defaultCountry || localeCountry;
  const addressState = selector(state, 'state') || get(billingAddress, '[0].state', null);
  const vatUpdateError = get(fee_profiles, 'vatAddress.updateError');
  const addressOptionSelected = selector(state, 'addressChoice');

  const displayBillingAddress = 'same-as-billing' === addressOptionSelected ? 'block' : 'none';
  //display the address form if they don't have an address on record
  const displayAddressForm = !address || ('different-address' === addressOptionSelected);

  let vatIdValidationKey;
  if (isProvincial) {
    addressState ? vatIdValidationKey = `${country}_${addressState}` :
    vatIdValidationKey = null;
  } else {
    vatIdValidationKey = country;
  }

  const taxIdLength = vatIdValidationKey ? vatIdLengthData[vatIdValidationKey] : null;

  const isPostingProfile = get(fee_profiles, 'vatAddress.isPosting', false);
  const isLoading = isPostingProfile;
  const editPrefilledValues = {
    ...(get(vatProfile, 'state') && {state: get(vatProfile, 'state')}),
    ...(get(vatProfile, 'unit') && {unit: get(vatProfile, 'unit')}),
    ...(isHost && {vat_percent: (get(vatProfile, 'vat_percent') * 100)}),
    addressChoice: 'different-address',
    business_name: get(vatProfile, 'business_name', ''),
    city: get(vatProfile, 'city', ''),
    street: get(vatProfile, 'street', ''),
    zip: get(vatProfile, 'zip', ''),
    vat_id: get(vatProfile, 'vat_id', '')
  };

  return {
    accessToken,
    envCountries,
    defaultCountry,
    country,
    addressState,
    displayBillingAddress,
    displayAddressForm,
    billingAddress,
    addressOptionSelected,
    taxIdLength,
    isLoading,
    vatUpdateError,
    localeCountry,
    isHost,
    initialValues: {
      addressChoice: useLocaleCountry ? 'different-address' : 'same-as-billing',
      country,
      ssoId,
      ...(isEdit && {...editPrefilledValues})
    }
  }
}

export default connect(mapStateToProps)(reduxForm(config)(injectIntl(TaxRegistrationForm)));
